//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { makeStyles, Chip } from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
// import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
import {
    ELASTICSEARCH_SORT_OPTIONS,
    LAT_LONG_ONLY_SEARCH_SORT_OPTIONS,
    SEARCH_WITH_LAT_LONG_SORT_OPTIONS,
    SEARCH_WITHOUT_LAT_LONG_SORT_OPTIONS,
    ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC,
    SEARCH_PUBLIC_INITIAL_SORT_OPTIONS,
} from '../../common/elasticSearchHelper';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { AppContext, LayoutContext } from '../../common/contexts';
import { getUniqueKey } from '../../common/arrayHelper';
import { getJobsFoundText, getSalaryRateFilterText } from '../../common/textHelper';
import {
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_DATE_POSTED,
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_INTERN_POSITIONS,
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_JOB_TYPE,
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_SALARY_RATE,
    ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_WORK_FROM_HOME,
    ANALYTICS_EVENT_SEARCH,
    ANALYTICS_EVENT_SEARCH_FILTER,
    FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES,
    PAGE_TITLE_JOB_CENTER,
    PLEASE_CHECK_BACK_VERY_SOON,
    THERE_ARE_NO_JOB_LISTINGS_TO_DISPLAY_AT_THE_MOMENT,
} from '../../common/constants';
import { fetchJobs, updateRecentSearches } from '../../common/services/jobCenterServices';
import { getNewJobCenterQuery, isNonLocationFiltersExistsInQuery, isQueryEmpty } from '../../common/queryHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdPagination from '../../common/elemental/AfitdPagination';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import DropDown from './DropDown';
import JobItem from './JobItem';
import FiltersBar from './FilterBar';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _map from 'lodash/map';
import _isEqual from 'lodash/isEqual';
import _forEach from 'lodash/forEach';
import _isNil from 'lodash/isNil';
import _cloneDeep from 'lodash/cloneDeep';
import _omit from 'lodash/omit';
import _size from 'lodash/size';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { isDefaultSearch } = params;

    return makeStyles(theme => ({
        root: {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        },
        chipBox: {
            padding: '10px 0px',
            display: 'flex',
            flexWrap: 'wrap',
            [theme.breakpoints.down('sm')]: { padding: '10px 15px' },
        },
        chip: {
            borderRadius: '5px',
            fontSize: '13px',
            border: '2px solid #1D8A8B',
            background: 'rgba(150,240,241,0.1)',
            margin: '4px',

            '& .MuiSvgIcon-root ': {
                height: '18px !important',
                fill: '#97A19F',
            },
        },
        jobsMainContainer: {
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            [theme.breakpoints.down('sm')]: { margin: '0px 15px' },
        },
        header: {
            display: 'flex',
            padding: '20px',
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: { padding: '20px 10px' },
        },
        jobsFound: {
            // textTransform: 'lowercase',
            fontSize: isDefaultSearch ? 20 : 18,
            fontWeight: 'bold',
            lineHeight: '21px',
            letterSpacing: '-1px',
        },
        sortBox: {
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            justifyContent: 'flex-end',
        },
        sortText: {
            padding: '0 8px',
        },
        sortDropDown: {
            maxWidth: '130px',
            color: '#000',
            [theme.breakpoints.down('sm')]: { maxWidth: '125px' },
            // borderRadius: '5px',
            // border: '2px solid #000',
            // boxShadow: 'none',
            // '&:hover': {
            //     content: 'unset',
            // },
            // '&:after, &:before': {
            //     content: 'unset',
            // },
        },
        searchItems: {
            flex: 1,
            // maxHeight: '65vh',
            overflow: 'scroll',
            '&::-webkit-scrollbar': {
                width: '0.4em',
            },
            '&::-webkit-scrollbar-track': {
                '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,.1)',
                borderRadius: '5px',
            },
        },
        searchFilterContainer: {
            [theme.breakpoints.only('lg')]: { display: 'flex' },
            [theme.breakpoints.down('sm')]: { padding: '0px 15px' },
        },
        paginationContainer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: 8,
        },
        filterSmContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            background: '#F0F2F6',
            borderRadius: 4,
            marginTop: 10,
        },
        filterSmItem1: {
            paddingLeft: 16,
        },
        filterSmItem2: {
            paddingRight: 19,
            display: 'flex',
            alignItems: 'center',
        },
        clearAllFiltersContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            [theme.breakpoints.down('sm')]: { marginRight: 16 },
        },
        clearAllFiltersText: {
            color: 'blue',
            cursor: 'pointer',
        },
        preSearchDiv: {
            paddingTop: '20px',
            // marginTop: '150px',
            // [theme.breakpoints.down('lg')]: {
            //     marginTop: '0px',
            // },
        },
        perSearchText: {
            color: 'grey',
            textAlign: 'center',
            marginTop: 10,
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        isDefaultSearch,
        sortTag,
        // setSortTag,
        handleChangeSortTag,
        handleClickJobItem,
        // isBrandPage,
        currentPageNumber,
        handleChangePage,
        jobs,
        // setJobs,
        searchResultTotal,
        // setSearchResultTotal,
        selectedJobId,
        handleClearFilters,
        onClickItem,
    } = params;

    const { query, setQuery, /*jobs, searchResultTotal = 0, selectedJob,*/ setShowDetails } = props;
    const paginationCount = Math.ceil(searchResultTotal / 10);

    const defns = {
        sortDropDown: {
            title: 'Date',
            options: getSortDropDownOptions({ query, isDefaultSearch }),
            value: sortTag,
            onChange: handleChangeSortTag,
        },
        sortText: {
            variant: 'body1',
        },
        jobsFound: {
            value: isDefaultSearch ? 'Job Center' : getJobsFoundText(searchResultTotal),
        },
        jobList: {
            jobs,
            selectedJobId,
            onJobItemClick: handleClickJobItem,
            setShowDetails,
            currentPageNumber,
            onClickItem,
        },
        filterBar: {
            query,
            setQuery,
        },
        jobSearchPagination: {
            count: paginationCount,
            color: 'secondary',
            page: currentPageNumber,
            onChange: handleChangePage,
        },
        clearAllFiltersText: {
            onClick: handleClearFilters,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const getSortDropDownOptions = ({ query, isDefaultSearch }) => {
    const lat = _get(query, 'location.value.location.lat');
    const lon = _get(query, 'location.value.location.lon');
    if (isDefaultSearch) {
        return SEARCH_PUBLIC_INITIAL_SORT_OPTIONS;
    }
    if (lat && lon) {
        return isNonLocationFiltersExistsInQuery(query) ? SEARCH_WITH_LAT_LONG_SORT_OPTIONS : LAT_LONG_ONLY_SEARCH_SORT_OPTIONS;
    } else {
        return SEARCH_WITHOUT_LAT_LONG_SORT_OPTIONS;
    }
};

// Job list
const JobList = props => {
    const { jobs = [], selectedJobId, onJobItemClick, setShowDetails, onClickItem, setSelectedJob } = props;

    // let end = currentPageNumber * ELASTICSEARCH_PAGE_SIZE;
    // let start = end - ELASTICSEARCH_PAGE_SIZE;
    // const slicedJobs = jobs.slice(start, end);
    const slicedJobs = jobs;
    const theme = useTheme();
    const xlMatches = useMediaQuery(theme.breakpoints.up('xl'));

    return _map(slicedJobs, job => {
        const isSelected = selectedJobId === job.uid;
        return (
            <div>
                {xlMatches ? (
                    <JobItem
                        job={job}
                        {...job}
                        isSelected={isSelected}
                        onClick={() => {
                            onClickItem && onClickItem();
                            onJobItemClick(job);
                            setSelectedJob && setSelectedJob(job);
                        }}
                    />
                ) : (
                    <div onClick={() => setShowDetails(true)}>
                        <JobItem
                            job={job}
                            {...job}
                            isSelected={isSelected}
                            onClick={() => {
                                onClickItem && onClickItem();
                                onJobItemClick(job);
                                setSelectedJob && setSelectedJob(job);
                            }}
                        />
                    </div>
                )}
            </div>
        );
    });
};

// Filter chip list
const FilterChipList = props => {
    const classes = useStyles()();
    const chipList = [];
    const { query, onDelete } = props;

    // job types
    const jobTypeList = query?.jobType || [];
    _forEach(jobTypeList, item => {
        item !== FILTER_OPTION_JOB_TYPE_ALL_JOB_TYPES && chipList.push({ name: 'jobType', value: item });
    });

    // date posted
    const datePost = query?.datePost || null;
    datePost && chipList.push({ name: 'datePost', value: datePost });

    // salary/rate
    const salaryRate = query?.salaryRate || null;
    if (salaryRate) {
        const rateMin = salaryRate.rateMin;
        const payFrequency = salaryRate.payFrequency;
        const currencySymbol = salaryRate.currencySymbol;

        if (!_isNil(rateMin) && !_isNil(payFrequency)) {
            chipList.push({ name: 'salaryRate', value: getSalaryRateFilterText(rateMin, currencySymbol, payFrequency) });
        }
    }

    // work from home
    const workFromHome = query?.workFromHome || false;
    workFromHome && chipList.push({ name: 'workFromHome', value: 'Work from Home' });

    // intern positions
    const internPositions = query?.internPositions || false;
    internPositions && chipList.push({ name: 'internPositions', value: 'Intern Positions' });

    // definition
    const defns = {
        chip: {
            deleteIcon: <CloseOutlined />,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    // render
    return chipList.map(item => (
        <div key={getUniqueKey()}>
            <Chip
                label={item.value}
                onDelete={() => {
                    onDelete && onDelete(item.name, item.value);
                }}
                {...defns.chip}
            />
        </div>
    ));
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SideBar = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { userId, esToken, setEsToken } = appContext;
    const layoutContext = useContext(LayoutContext);
    const { isBrandPage, recentSearches, setIsRequestOpenLoginDialog, searchResultTotal = 0, setSearchResultTotal, setAsyncCallCount } = layoutContext;
    const theme = useTheme();
    const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    const myRef = useRef(null);
    const lastQuery = useRef();
    const lastQueryPageNumber = useRef();
    // const history = useHistory();
    // const location = useLocation();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
    const [jobs, setJobs] = useState([]);
    // const [searchResultTotal, setSearchResultTotal] = useState(0);
    const [selectedJobId, setSelectedJobId] = useState();
    const [initialLoad, setInitialLoad] = useState(true);
    // const [hideResults, setHideResults] = React.useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const {
        onDeleteChip,
        query,
        sortTag,
        setSortTag,
        selectedJob,
        setSelectedJob,
        showFilterPage,
        setShowFilterPage,
        setQuery,
        queryInitState,
        onClickItem,
        searchedPerformed,
        setSearchPerformed,
        setShowAds,
        setShowDetails,
        setBrandPageActiveJobCount,
    } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isDefaultSearch = _isEqual(_omit(query, ['sortTag']), _omit(queryInitState, ['sortTag']));

    //---------------------------------------------------------------------
    // ElasticSearch
    //---------------------------------------------------------------------
    const searchJobs = useCallback(
        async ({ page = 1, isSelectFirstJob, forceQuery } = {}) => {
            setAsyncCallCount && setAsyncCallCount(prev => prev + 1);
            const searchResult = await fetchJobs({
                // setList: (jobs = []) => {
                //     setJobs(jobs);
                // },
                queryObject: forceQuery || query,
                from: page,
                esToken,
                setEsToken,
            });
            setSearchPerformed(true);
            // setHideResults(false);

            const total = _get(searchResult, 'total', 0);
            setSearchResultTotal(total);
            const jobList = _get(searchResult, 'list', []);
            setJobs(jobList);

            // only if parent is Brand Page and assume brand page tier 3 always load all active jobs and no search functionality
            if (isBrandPage) {
                // if active jobs not loaded yet, select the first job
                if (!selectedJob && jobList.length > 0) {
                    const firstJob = jobList[0];
                    setSelectedJob && setSelectedJob(firstJob);
                    setSelectedJobId(firstJob.uid);
                }

                // set the total count
                setBrandPageActiveJobCount && setBrandPageActiveJobCount(total);
            } else {
                // if not brand page, log events
                const searchTerm = query?.jobTitle || '_BLANK_';
                logEvent(ANALYTICS_EVENT_SEARCH, { search_term: searchTerm, page_title: PAGE_TITLE_JOB_CENTER });

                _size(query?.jobType) > 0 && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_JOB_TYPE });
                query?.datePost && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_DATE_POSTED });
                query?.salaryRate && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_SALARY_RATE });
                query?.workFromHome && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_WORK_FROM_HOME });
                query?.internPositions && logEvent(ANALYTICS_EVENT_SEARCH_FILTER, { filter_name: ANALYTICS_EVENT_PARAM_SEARCH_FILTER_JOB_CENTER_INTERN_POSITIONS });
            }

            if (isSelectFirstJob && jobList.length > 0) {
                const firstJob = jobList[0];
                setSelectedJob(firstJob);
                setSelectedJobId(firstJob.uid);
                setShowDetails(true);
            }
            // myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

            setAsyncCallCount && setTimeout(() => setAsyncCallCount(prev => prev - 1), 500);
        },
        [
            query,
            esToken,
            setEsToken,
            setSearchPerformed,
            setSelectedJob,
            setShowDetails,
            isBrandPage,
            selectedJob,
            setBrandPageActiveJobCount,
            setSearchResultTotal,
            setAsyncCallCount,
        ],
    );

    //---------------------------------------------------------------------
    // effect
    //---------------------------------------------------------------------
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: sortTag changed');
    // }, [sortTag]);
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: selectedJobId changed');
    // }, [selectedJobId]);
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: setSelectedJob changed');
    // }, [setSelectedJob]);
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: esToken changed');
    // }, [esToken]);
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: setEsToken changed');
    // }, [setEsToken]);
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: searchJobs changed');
    // }, [searchJobs]);
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: currentPageNumber changed');
    // }, [currentPageNumber]);
    // useEffect(() => {
    //     console.log('------->>>>>> side effect: query changed');
    // }, [query]);

    // when query or page number has changed, execute search
    useEffect(() => {
        //TODO Remove this condition when we are not loading all jobs on page load
        if (initialLoad && isDefaultSearch) {
            searchJobs({ page: 1, isSelectFirstJob: false, forceQuery: queryInitState });
            setInitialLoad(false);

            // remember the query and the page number
            lastQuery.current = _cloneDeep(query);
            lastQueryPageNumber.current = currentPageNumber;
            ////
            //TODO Uncomment this when we are not loading all jobs on page load
            // } else if (!_isEqual(query, queryInitState)) {
        } else {
            if (query && currentPageNumber) {
                if (!userId && currentPageNumber > 2) {
                    setCurrentPageNumber(lastQueryPageNumber.current);
                    setIsRequestOpenLoginDialog(true);
                } else {
                    const sameQuery = _isEqual(lastQuery.current, query);
                    const samePageNumber = lastQueryPageNumber.current === currentPageNumber;
                    const singleJobQuery = query?.id;

                    // if query is same as last, do not search
                    if (lastQuery && lastQueryPageNumber && (!sameQuery || !samePageNumber)) {
                        if (isBrandPage) {
                            console.log('############################### Brand Page Active Jobs searching (query changed)', { query });

                            searchJobs({ page: currentPageNumber, isSelectFirstJob: false }); //TODO When re-running search, should we keep selected job on the right pane?
                        } else {
                            console.log('############################### Job Center searching (query changed)', { query });

                            searchJobs({ page: currentPageNumber, isSelectFirstJob: singleJobQuery }); //TODO When re-running search, should we keep selected job on the right pane?

                            // update the Recent Searches
                            if (userId && !isQueryEmpty(query) && !sameQuery && !singleJobQuery) {
                                const prevSearches = [...recentSearches];
                                if (prevSearches.length >= 5) prevSearches.shift();
                                updateRecentSearches({ userId, recentSearches: [...prevSearches, query] });
                            }
                        }

                        // remember the query and the page number
                        lastQuery.current = _cloneDeep(query);
                        lastQueryPageNumber.current = currentPageNumber;
                    }
                }
            }
        }
    }, [
        isBrandPage,
        initialLoad,
        isDefaultSearch,
        query,
        currentPageNumber,
        searchJobs,
        userId,
        recentSearches,
        lastQuery,
        lastQueryPageNumber,
        queryInitState,
        setIsRequestOpenLoginDialog,
    ]);

    // Uncomment this if we want to automatically select first job (assuming no selected job and Ads are disabled) -el 20221012
    // // select first job by default if job list has changed and no selected job
    // useEffect(() => {
    //     if (!selectedJobId && jobs.length > 0) {
    //         const firstJob = jobs[0];
    //         setSelectedJob(firstJob);
    //         setSelectedJobId(firstJob.uid);
    //         setShowDetails(true);
    //         setShowAds(false);
    //     }
    // }, [jobs, selectedJobId, setSelectedJob, setSelectedJobId]);

    // if selectedJob has changed, set selectedJobId
    useEffect(() => {
        if (selectedJob) {
            const jobId = selectedJob.uid;
            setSelectedJobId(jobId);
        }
    }, [selectedJob]);

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    // pagination section
    // const currentLocation = location.pathname.slice(0, 5) === '/dash' ? '/dashboard/2' : '/job-center';
    const handleChangePage = (event, value) => {
        // setCurrentPageNumber(value);
        // history.push(`${currentLocation}/${value}`);
        // onPageChange && onPageChange(value);
        setCurrentPageNumber(value);
    };

    const handleChangeSortTag = val => {
        setQuery(prevQuery => getNewJobCenterQuery({ prevQuery, newQuery: { sortTag: ELASTICSEARCH_SORT_OPTIONS[val] } }));
        setSortTag(val);
    };

    const handleDeleteChip = (name, value) => {
        onDeleteChip && onDeleteChip(name, value);
    };

    const handleClickJobItem = job => {
        const jobId = _get(job, 'uid');
        setSelectedJob(job);
        setSelectedJobId(jobId);
    };

    const handleClearFilters = () => {
        console.log('removing filters');
        setQuery(queryInitState);

        //TODO Change this back to ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT when we are not loading all jobs on page load
        setSortTag(ELASTICSEARCH_SORT_OPTION_UPDATE_DATE_DESC);
        // setSortTag(ELASTICSEARCH_SORT_OPTION_MOST_RELEVANT);

        setSearchPerformed(false);
        // setHideResults(true);
        // Make Ads Display Again.....
        setShowAds(true);
        // Make Ads Display Again.....

        //TODO Remove this when we are not loading all jobs on page load
        searchJobs({ page: 1, isSelectFirstJob: false, isHideResultCount: true, forceQuery: queryInitState });
    };

    const clearAllFiltersContainerIsHidden = _isEqual(query, queryInitState);

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ isBrandPage, isDefaultSearch })();
    const defns = getDefns({
        classes,
        props,
        isDefaultSearch,
        sortTag,
        setSortTag,
        handleChangeSortTag,
        handleClickJobItem,
        isBrandPage,
        currentPageNumber,
        handleChangePage,
        jobs,
        setJobs,
        searchResultTotal,
        setSearchResultTotal,
        selectedJobId,
        handleClearFilters,
        onClickItem,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            {showFilterPage && smMatches ? (
                <FiltersBar {...defns.filterBar} setShowFilterPage={setShowFilterPage} setShowAds={setShowAds} />
            ) : (
                <div {...defns.root}>
                    {!isBrandPage && (
                        <div {...defns.searchFilterContainer}>
                            {lgMatches ? (
                                smMatches ? (
                                    <div {...defns.filterSmContainer} onClick={() => setShowFilterPage(true)}>
                                        <div {...defns.filterSmItem1}>
                                            <p style={{ fontSize: '15px' }}>Filter</p>
                                        </div>
                                        <div {...defns.filterSmItem2}>
                                            <KeyboardArrowDownIcon />
                                        </div>
                                    </div>
                                ) : null
                            ) : // <FiltersBar {...defns.filterBar} setShowAds={setShowAds} />
                            null}
                        </div>
                    )}

                    {!isBrandPage && (
                        <>
                            <div {...defns.chipBox}>
                                <FilterChipList query={query} onDelete={handleDeleteChip} />
                            </div>
                            {!clearAllFiltersContainerIsHidden && (
                                <div {...defns.clearAllFiltersContainer}>
                                    <AfitdTypography {...defns.clearAllFiltersText}>Clear all filters</AfitdTypography>
                                </div>
                            )}
                        </>
                    )}

                    <div {...defns.jobsMainContainer}>
                        {!searchedPerformed ? (
                            <div {...defns.preSaveHeader}>
                                <div {...defns.preSearchDiv}>
                                    <AfitdTypography variant="h6" align="center">
                                        <b>Job Center</b>
                                    </AfitdTypography>
                                    <AfitdTypography {...defns.perSearchText}>Please use the search box or filters to perform a search</AfitdTypography>
                                </div>
                            </div>
                        ) : lgMatches && isDefaultSearch && _size(jobs) === 0 ? (
                            <div {...defns.preSaveHeader}>
                                <div {...defns.preSearchDiv}>
                                    <AfitdTypography variant="h6" align="center">
                                        <b>{THERE_ARE_NO_JOB_LISTINGS_TO_DISPLAY_AT_THE_MOMENT}</b>
                                    </AfitdTypography>
                                    <AfitdTypography {...defns.perSearchText}>{PLEASE_CHECK_BACK_VERY_SOON}</AfitdTypography>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div {...defns.header}>
                                    <div>
                                        <AfitdTypography {...defns.jobsFound} />
                                    </div>
                                    {!isBrandPage && (
                                        <div {...defns.sortBox}>
                                            <AfitdTypography {...defns.sortText}>Sort by:</AfitdTypography>
                                            <DropDown {...defns.sortDropDown} />
                                        </div>
                                    )}
                                </div>
                                <div {...defns.searchItems}>
                                    <div ref={myRef} />
                                    <JobList {...defns.jobList} />
                                    <div
                                        {...defns.paginationContainer}
                                        onClick={() => {
                                            setTimeout(() => {
                                                myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }, 100);
                                        }}
                                    >
                                        <AfitdPagination {...defns.jobSearchPagination} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default React.memo(SideBar);
